import {
  WSActions,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayoutPreferences } from "@wingspanhq/payments/dist/interfaces";
import {
  AccountExpand,
  PayoutDestinationType
} from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useMemo, useState } from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import { WSQueries } from "../../../../query/WSQuery";
import {
  queriesAccounts,
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesPaymentCards,
  queriesPayouts
} from "@query";
import { useSession } from "@hooks/useSession";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorExternalBankAccountIPReady } from "@selectors/selectorExternalBankAccountIPReady";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { isCountryUS } from "../../../../shared/selectors/selectorIsDomesticMember";
import { selectorPaymentCardPayoutReady } from "../../../../shared/selectors/selectorPaymentCardPayoutReady";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { selectorIsPlatformBalanceAccount } from "../../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { selectorPayoutSettingsUpdateForMethod } from "../../selectors/selectorPayoutSettingsUpdateForMethod";
import { List } from "../ManageMethods/List";
import { MethodValue } from "../ManageMethods/types";
import { useModalConfirmPayoutMethodChange } from "../ModalConfirmPayoutMethodChange";
import { useDefaultPayoutMethod } from "./DefaultPayoutMethod";
import { useModalInfo } from "./ModalInfo";
import { selectorInstantPayoutFeeLabel } from "@selectors/selectorInstantPayoutFeeLabel";
import { useInstantPayoutFee } from "../../hooks/useInstantPayoutFee";
import { selectorExternalBankAccountPayoutReady } from "@selectors/selectorExternalBankAccountPayoutReady";

type Props = {
  onSaved: () => void;
  onAddNew: () => void;
  error?: WSServiceError;
};

export const ManagePayoutMethod: React.FC<Props> = props => {
  const { accountId } = useSession();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryPaymentCards = queriesPaymentCards.listPaymentCards.useQuery();
  const queryAccount = queriesAccounts.getAccount.useQuery(
    { accountId, expand: [AccountExpand.COMPLIANCE_ENTITY] },
    { enabled: !!accountId }
  );
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryFeatureFlags = useQueryFeatureFlags();

  const [value, setValue] = useState<MethodValue | null | undefined>();

  const { method: defaultPayoutMethod } = useDefaultPayoutMethod();
  useEffect(() => {
    if (defaultPayoutMethod) {
      setValue(defaultPayoutMethod);
    }
  }, [defaultPayoutMethod]);

  const country = queryAccount.data?.complianceEntity?.address?.country;

  const isInternational = queryAccount.data ? !isCountryUS(country) : false;

  // A platform-balance destination is instant-capable regardless of country.
  const isInternationalInstantPayoutReady =
    (queryPayoutSettings.data
      ? selectorPayoutSettingsDefaultDestination(queryPayoutSettings.data)?.type
      : undefined) === PayoutDestinationType.INTERNAL_ACCOUNT ||
    country === "CA";

  const showInstantPayoutMethods = isInternational
    ? queryFeatureFlags.data?.internationalIP &&
      isInternationalInstantPayoutReady
    : true;
  const ipFee = useInstantPayoutFee();
  const modalInfo = useModalInfo();

  const [updatePayoutSettings, updatePayoutSettingsMeta] =
    queriesPayouts.updatePayoutSettings.useMutation({
      onSuccess: props.onSaved
    });

  const payoutSettingsId = queryPayoutSettings.data?.id;

  const save = (method: MethodValue | null | undefined) => {
    const platformBalanceAccountId = queryInternalAccounts.data?.data?.find(
      internalAccount =>
        selectorIsPlatformBalanceAccount(
          internalAccount,
          queryPayoutSettings.data
        )
    )?.id;

    const payoutSettingsUpdate = selectorPayoutSettingsUpdateForMethod({
      method,
      platformBalanceAccountId
    });

    if (!payoutSettingsId || !payoutSettingsUpdate) {
      return;
    }

    updatePayoutSettings({ payoutSettingsId, payoutSettingsUpdate });
  };

  const modalConfirmPayoutMethodChange = useModalConfirmPayoutMethodChange();

  const isSameMethodSelected = useMemo(
    () =>
      value?.id === defaultPayoutMethod?.id &&
      value?.category === defaultPayoutMethod?.category,
    [value, defaultPayoutMethod]
  );

  return (
    <WSQueries
      queries={{
        queryExternalBankAccounts,
        queryPaymentCards,
        queryInternalAccounts,
        queryPayoutSettings
      }}
    >
      {({
        queryExternalBankAccounts: { data: externalBankAccountList },
        queryPaymentCards: { data: paymentCardList },
        queryInternalAccounts: { data: internalAccounts },
        queryPayoutSettings: { data: payoutSettings }
      }) => {
        return (
          <WSList gap="XL">
            {/* Surface the upstream error (e.g. failed save the user came back from) */}
            <ErrorAlert context="PayoutSettings" error={props.error} />

            <WSElement>
              <WSText weight="medium" mb="M">
                Standard payout methods
              </WSText>

              <List
                data={{
                  accounts: (externalBankAccountList.data || []).filter(
                    selectorExternalBankAccountPayoutReady
                  ),
                  internalAccounts: (internalAccounts.data || []).filter(
                    internalAccount =>
                      selectorActiveBankingAccount(internalAccount) ||
                      selectorIsPlatformBalanceAccount(
                        internalAccount,
                        payoutSettings
                      )
                  )
                }}
                onChange={setValue}
                value={value}
                category={PayoutPreferences.Standard}
                initialValue={defaultPayoutMethod}
              />

              {showInstantPayoutMethods && (
                <>
                  <WSDivider mt="2XL" mb="3XL" />

                  <WSFlexBox gap="S" alignItems="center" mb="M">
                    <WSText weight="medium">
                      {selectorInstantPayoutFeeLabel({
                        text: "Instant payout methods",
                        feePercentage: ipFee
                      })}
                    </WSText>

                    <WSIcon
                      name="info-circle-fill"
                      color="gray500"
                      size="S"
                      onClick={() => {
                        modalInfo.open();
                      }}
                    />
                  </WSFlexBox>

                  <List
                    data={{
                      accounts: (externalBankAccountList.data || []).filter(
                        externalBankAccount =>
                          selectorExternalBankAccountIPReady(
                            externalBankAccount,
                            isInternational
                          )
                      ),
                      paymentCards: (paymentCardList.data || []).filter(
                        selectorPaymentCardPayoutReady
                      ),
                      internalAccounts: (internalAccounts.data || []).filter(
                        internalAccount =>
                          selectorActiveBankingAccount(internalAccount) ||
                          selectorIsPlatformBalanceAccount(
                            internalAccount,
                            payoutSettings
                          )
                      )
                    }}
                    onChange={setValue}
                    value={value}
                    category={PayoutPreferences.Instant}
                    initialValue={defaultPayoutMethod}
                  />
                </>
              )}
            </WSElement>

            <WSCard
              onClick={props.onAddNew}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    icon: "plus-circle",
                    kind: "Link",
                    label: "Add new payout method"
                  }
                ]
              }}
            />

            <ErrorAlert
              context="PayoutSettings"
              error={updatePayoutSettingsMeta.error}
            />

            <WSActions
              alignment="fill"
              buttons={[
                {
                  label: "Save changes",
                  onClick: () => {
                    if (!defaultPayoutMethod) {
                      save(value);
                      return;
                    }
                    modalConfirmPayoutMethodChange.open({
                      onSave: () => {
                        save(value);
                      },
                      previousMethod: defaultPayoutMethod,
                      newMethod: value!
                    });
                  },
                  loading: updatePayoutSettingsMeta.isLoading,
                  disabled: isSameMethodSelected
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};
